import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Instagram, Facebook, Linkedin, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

const Awards = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title={t('awards.title') + ' ' + t('awards.titleHighlight') + ' ' + t('awards.titleRest')}
        description="AITIM Awards Ceremony"
        canonical="/awards"
      />
      <Navbar />

      <main>
        {/* Top Hero Logo */}
        <section className="bg-black pt-28 pb-12 md:pt-48 md:pb-20 flex justify-center items-center">
          <img 
            src="https://static.wixstatic.com/media/5c8a3f_0c6cf619c0ee4da3894e5d0a641020e5~mv2.png/v1/fill/w_239,h_230,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/INSTAGRAM%20%D0%B8%D1%81%D1%82%D0%BE%D1%80%D0%B8%D1%8F%20AWARDS%2C%20%D0%BA%D0%BE%D0%BF%D0%B8%D1%8F.png" 
            alt="AITIM Awards" 
            className="w-48 md:w-64 h-auto"
          />
        </section>

        {/* Nominations Info */}
        <section className="bg-white py-16 md:py-24">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-black mb-8 leading-tight">
              {t('awards.title')} <span className="text-[#DAA520]">{t('awards.titleHighlight')}</span> {t('awards.titleRest')}
            </h1>
            <p 
              className="text-base md:text-lg text-gray-700 leading-relaxed mb-10 text-left md:text-center"
              dangerouslySetInnerHTML={{ __html: t('awards.desc') }}
            />
            
            <div className="flex justify-center items-center gap-4">
              <a href="https://www.instagram.com/aitim_group/" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-full hover:bg-[#DAA520] transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/aitiminc/" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-full hover:bg-[#DAA520] transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.linkedin.com/company/aitim" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-full hover:bg-[#DAA520] transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://t.me/aitim_sales" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-full hover:bg-[#DAA520] transition-colors">
                <Send className="w-5 h-5" />
              </a>
            </div>
          </div>
        </section>

        {/* Voting Closed Banner */}
        <section className="bg-black py-20 px-4">
          <div className="container mx-auto max-w-5xl text-center">
            <h2 className="text-2xl md:text-4xl font-medium text-[#DAA520] leading-snug">
              {t('awards.votingClosed')}
            </h2>
          </div>
        </section>

        {/* Learn About Section */}
        <section className="bg-[#111111]">
          <div className="flex flex-col lg:flex-row">
            {/* Left Content */}
            <div className="bg-white lg:w-1/2 p-12 md:p-24 flex flex-col justify-center items-start lg:items-center text-left lg:text-center">
              <div className="max-w-lg w-full">
                <h2 className="text-2xl md:text-3xl font-bold text-[#DAA520] mb-6 uppercase tracking-wider">
                  {t('awards.learnAbout')}
                </h2>
                <p className="text-black font-medium leading-relaxed mb-10">
                  {t('awards.learnDesc')}
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <Button className="bg-[#DAA520] hover:bg-[#B8860B] text-black font-bold px-8 py-6 rounded-md w-full sm:w-auto text-sm tracking-wider" aria-label={`${t('awards.learnBtn')} about AITIM Group`}>
                    {t('awards.learnBtn')}
                  </Button>
                  <div className="flex items-center gap-3">
                    <a href="https://www.instagram.com/aitim_group/" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-full hover:bg-[#DAA520] transition-colors">
                      <Instagram className="w-4 h-4" />
                    </a>
                    <a href="https://www.facebook.com/aitiminc/" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-full hover:bg-[#DAA520] transition-colors">
                      <Facebook className="w-4 h-4" />
                    </a>
                    <a href="https://www.linkedin.com/company/aitim" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-full hover:bg-[#DAA520] transition-colors">
                      <Linkedin className="w-4 h-4" />
                    </a>
                    <a href="https://t.me/aitim_sales" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-full hover:bg-[#DAA520] transition-colors">
                      <Send className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Image/Logo */}
            <div className="lg:w-1/2 relative min-h-[400px]">
              <img 
                src="https://static.wixstatic.com/media/5c8a3f_763bd31aa57f43179702119aab2aa26c~mv2.jpg/v1/fill/w_588,h_359,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/5c8a3f_763bd31aa57f43179702119aab2aa26c~mv2.jpg" 
                alt="AITIM Group Gold Logo" 
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Gallery Image */}
        <section className="bg-white py-16 md:py-24">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="rounded-xl overflow-hidden shadow-2xl relative group">
              <img 
                src="https://static.wixstatic.com/media/5c8a3f_507606e801d648a88f6aaa94bc3df8ac~mv2.jpg/v1/fill/w_980,h_576,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/5c8a3f_507606e801d648a88f6aaa94bc3df8ac~mv2.jpg" 
                alt="AITIM Awards Ceremony" 
                className="w-full h-auto object-cover transform transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Awards;
